<template>
  <div
    class="oct-text-field"
    :class="{
      'oct-text-field--block': block
    }"
  >
    <input
      class="oct-text-field__input"
      :id="id"
      :type="type === 'postal-code' ? 'text' : type"
      :autocomplete="type === 'number' ? false : type"
      :placeholder="placeholder"
      :size="cols*2"
      :disabled="disabled"
      :required="required"
      :aria-invalid="invalid.toString()"
      v-model.lazy="$_octTextField_value"
      v-if="rows <= 1"
      @change="$emit('change', $event)"
    >

    <textarea
      class="oct-text-field__input"
      :placeholder="placeholder"
      :rows="rows"
      :cols="cols*2"
      :disabled="disabled"
      :required="required"
      :aria-invalid="invalid.toString()"
      v-model.lazy="$_octTextField_value"
      v-else
      @change="$emit('change', $event)"
    ></textarea>

    <div class="oct-text-field-helper-line" v-if="helperText !== '' || (invalid && helperTextInvalid !== '')">
      <span class="oct-text-field-helper-text" aria-hidden="true">{{ invalid ? helperTextInvalid : helperText }}</span>
    </div>

    <div
      class="oct-text-field-helper-line"
      v-else-if="$slots.helperText"
    >
      <span
        class="oct-text-field-helper-text"
        aria-hidden="true"
      >
        <slot name="helperText" />
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OctTextField',
  props: {
    /** Id attribute for native control */
    id: {
      type: [String,Number],
      default: null
    },
    /** Text Value */
    value: {
      type: [String,Number],
      default: ''
    },
    /** Placeholder Text */
    placeholder: {
      type: String,
      default: ''
    },
    /** Text field width */
    cols: {
      type: Number,
      default: 20
    },
    /** Whether enable multiline input */
    rows: {
      type: Number,
      default: 1
    },
    /** Helper text */
    helperText: {
      type: String,
      default: ''
    },
    /** Invalid helper text */
    helperTextInvalid: {
      type: String,
      default: ''
    },
    /** Input type [number|email|tel|postal-code] */
    type: {
      type: String,
      default: 'text'
    },
    /** External validation function */
    validator: {
      type: Function,
      default: null
    },
    /** Native disabled attribute */
    disabled: {
      type: Boolean,
      default: false
    },
    /** Whether required */
    required: {
      type: Boolean,
      default: false
    },
    /** Renderd as block element */
    block: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      invalid: false,
    }
  },
  computed: {
    $_octTextField_value: {
      get () {
        return this.value
      },
      async set (val) {
        // Validate and emit
        this.invalid = !(await this.validate(val))
        this.$emit('input', val)
      }
    }
  },
  methods: {
    /** Input validation */
    async validate (val) {
      // Empty check
      let invalid = this.required && !val

      // Number check
      invalid = (this.type === 'number' && !this.validateNumber(val)) || invalid

      // Email check
      invalid = (this.type === 'email' && !this.validateEmail(val)) || invalid

      // Finally do the external validation
      if (!invalid && this.validator)
        invalid = !await this.validator(val)

      return !invalid
    },
    /** Number validateion */
    validateNumber (val) {
      return /^[.0-9]+$/.test(val)
    },
    /** Email validateion */
    validateEmail (val) {
      return /[a-zA-Z0-9]+[a-zA-Z0-9._-]*@[a-zA-Z0-9_-]+[a-zA-Z0-9._-]+/.test(val)
    }
  }
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.oct-text-field {
  display: inline-block;

  &--block {
    display: block;
    width: 100%;
  }
}

.oct-text-field__input {
  font-family: $font-family-sans-serif !important;
  font-size: oct-rem(15);
  line-height: (24/15);
  margin: 0 0 oct-rem(9);
  padding: oct-rem(10-1) oct-rem(16-1);
  background-color: transparent;
  border: oct-rem(1) solid $oct-theme--primary;
  border-radius: oct-rem(6);
  appearance: none;
  outline: none;

  .oct-text-field--block & {
    width: 100%;
  }

  &[type="number"] {
    -moz-appearance:textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button
    {
      appearance: none;
      margin: 0;
    }

    &[size="2"] {
      width: oct-rem(54);
    }

    &[size="3"] {
      width: oct-rem(68);
    }

    &[size="4"] {
      width: oct-rem(80);
    }

    &[size="5"] {
      width: oct-rem(120);
    }
  }

  &:last-child {
    margin-bottom: 0;
  }

  &:focus {
    padding: oct-rem(10-3) oct-rem(16-3);
    border-width: oct-rem(3);
  }

  &::placeholder {
    opacity: .55;
  }

  &:focus {

    &::placeholder {
      opacity: 0;
    }
  }

  &[disabled] {
    color: $oct-theme--primary;
    opacity: .4;
  }

  &[aria-invalid="true"] {
    border-color: $oct-theme--danger;
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(17);
  line-height: (28/17);
  margin: 0 0 oct-rem(12);
  padding: oct-rem(10-1) oct-rem(16-1);
  border-radius: oct-rem(8);

  &[type="number"] {
    -moz-appearance:textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button
    {
      appearance: none;
      margin: 0;
    }

    &[size="2"] {
      width: oct-rem(54);
    }

    &[size="3"] {
      width: oct-rem(68);
    }

    &[size="4"] {
      width: oct-rem(80);
    }

    &[size="5"] {
      width: oct-rem(120);
    }
  }
}
}

.oct-text-field-helper-line {
  opacity: .55;

  [aria-invalid="true"] + & {
    color: $oct-theme--danger;
    opacity: 1;
  }
}

.oct-text-field-helper-text {
  font-size: oct-rem(13);
  line-height: (14/13);
  margin: (-2em/13) 0;
  display: block;
}

</style>
